import { PAGE_SIZES_MM, PX_PER_MM } from '../constants';
import { ReportPageSetup } from '../types';

export type PageGeometry = {
  /** Full sheet, margins included. */
  sheetWidthMm: number;
  sheetHeightMm: number;
  /** Area left for content once both margins are taken off. */
  printableWidthMm: number;
  printableHeightMm: number;
  printableHeightPx: number;
};

/** Bottom band occupied by the automatic "n / total" page number. */
export const REPORT_PAGE_NUMBER_BOTTOM_MM = 8;

export const resolvePaginatedBottomPaddingMm = (
  marginMm: number,
  pageCount: number
): number =>
  pageCount > 1
    ? Math.max(marginMm, REPORT_PAGE_NUMBER_BOTTOM_MM)
    : marginMm;

/** Part of the page-number band not already covered by the chosen margin. */
export const resolvePaginationReserveHeightPx = (marginMm: number): number =>
  Math.max(0, REPORT_PAGE_NUMBER_BOTTOM_MM - marginMm) * PX_PER_MM;

/**
 * Sheet and content dimensions for a page setup.
 *
 * Both dimensions matter: a report has to be laid out as an actual sheet of
 * paper, so the preview shows what will come out of the printer. Width alone
 * makes it an endless strip.
 */
export const resolvePageGeometry = (page: ReportPageSetup): PageGeometry => {
  const size = PAGE_SIZES_MM[page.size] ?? PAGE_SIZES_MM.A4;
  const isLandscape = page.orientation === 'landscape';
  const sheetWidthMm = isLandscape ? size.height : size.width;
  const sheetHeightMm = isLandscape ? size.width : size.height;
  const printableWidthMm = Math.max(sheetWidthMm - 2 * page.marginMm, 1);
  const printableHeightMm = Math.max(sheetHeightMm - 2 * page.marginMm, 1);

  return {
    sheetWidthMm,
    sheetHeightMm,
    printableWidthMm,
    printableHeightMm,
    printableHeightPx: printableHeightMm * PX_PER_MM,
  };
};
